import $ from 'jquery';

import './styles/main.scss'

/**
 * Frontend Video Player for Vendor Product Videos
 *
 * @package
 * @since 1.0.0
 */
class DokanProductVideoPlayer {
	constructor() {
		this.players = [];
		this.init();
	}

	/**
	 * Initialize the video player
	 */
	init() {
		this.initNavigation();
		this.initVideoPlayers();
	}

	/**
	 * Initialize video tab navigation
	 */
	initNavigation() {
		const videoContainers = document.querySelectorAll( '.dokan-product-videos-display' );

		videoContainers.forEach( ( container ) => {
			const tabs = container.querySelectorAll( '.dokan-video-nav-tab' );
			const videos = container.querySelectorAll( '.dokan-product-video-item' );

			// Skip if no tabs or videos
			if ( ! tabs.length || ! videos.length ) {
				return;
			}

			// Add click handlers to tabs
			tabs.forEach( ( tab ) => {
				tab.addEventListener( 'click', () => {
					const videoIndex = tab.dataset.videoIndex;

					// Reset active state
					tabs.forEach( ( t ) => t.classList.remove( 'active' ) );
					videos.forEach( ( v ) => {
						v.classList.remove( 'active' );
						v.style.display = 'none';

						// Pause any playing videos
						this.pauseAllVideos( v );
					} );

					// Set active state
					tab.classList.add( 'active' );

					// Show selected video
					const selectedVideo = container.querySelector( `.dokan-product-video-item[data-video-index="${ videoIndex }"]` );
					if ( selectedVideo ) {
						selectedVideo.classList.add( 'active' );
						selectedVideo.style.display = 'block';
					}
				} );
			} );
		} );
	}

	/**
	 * Initialize video players
	 */
	initVideoPlayers() {
		// Self-hosted videos
		this.initSelfHostedPlayers();

		// Responsive behavior
		window.addEventListener( 'resize', this.handleResize.bind( this ) );
		this.handleResize();
	}

	/**
	 * Initialize self-hosted video players
	 */
	initSelfHostedPlayers() {
		const selfHostedVideos = document.querySelectorAll( '.dokan-video-player-selfhosted video' );

		selfHostedVideos.forEach( ( video ) => {
			// Create player reference
			const player = {
				id: `video_${ Math.random().toString( 36 ).substring( 2, 9 ) }`,
				element: video,
				type: 'selfhosted',
				container: video.closest( '.dokan-video-player-wrapper' ),
			};

			// Add to players array
			this.players.push( player );

			// Add play button overlay
			this.addPlayButtonOverlay( player );
		} );
	}

	/**
	 * Add play button overlay to video
	 *
	 * @param {Object} player - Player object
	 */
	addPlayButtonOverlay( player ) {
		if ( ! player.container ) {
			return;
		}

		// Create overlay if it doesn't exist
		let overlay = player.container.querySelector( '.dokan-video-play-overlay' );

		if ( ! overlay ) {
			overlay = document.createElement( 'div' );
			overlay.className = 'dokan-video-play-overlay';
			overlay.innerHTML = '<button class="dokan-video-play-button" aria-label="Play video"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="64" height="64"><path fill="currentColor" d="M8 5v14l11-7z"/></svg></button>';
			player.container.appendChild( overlay );
		}

		// Add click handler
		overlay.addEventListener( 'click', () => {
			this.playVideo( player );
		} );

		// Add ended event to show overlay again
		player.element.addEventListener( 'ended', () => {
			overlay.style.display = 'flex';
		} );

		// Add pause event to show overlay again
		player.element.addEventListener( 'pause', () => {
			overlay.style.display = 'flex';
		} );

		// Add play event to hide overlay
		player.element.addEventListener( 'play', () => {
			overlay.style.display = 'none';
		} );
	}

	/**
	 * Play a video
	 *
	 * @param {Object} player - Player object
	 */
	playVideo( player ) {
		// Pause all other videos first
		this.players.forEach( ( p ) => {
			if ( p.id !== player.id && p.element ) {
				p.element.pause();
			}
		} );

		// Play this video
		if ( player.element ) {
			player.element.play();
		}
	}

	/**
	 * Pause all videos in a container
	 *
	 * @param {Element} container - Container element
	 */
	pauseAllVideos( container ) {
		const videos = container.querySelectorAll( 'video' );
		videos.forEach( ( video ) => {
			video.pause();
		} );

		// Also handle iframe videos
		const iframes = container.querySelectorAll( 'iframe' );
		iframes.forEach( ( iframe ) => {
			// Try to pause YouTube videos
			if ( iframe.src.includes( 'youtube.com' ) || iframe.src.includes( 'youtu.be' ) ) {
				try {
					iframe.contentWindow.postMessage( '{"event":"command","func":"pauseVideo","args":""}', '*' );
				} catch ( e ) {
					// Ignore errors
				}
			}

			// Try to pause Vimeo videos
			if ( iframe.src.includes( 'vimeo.com' ) ) {
				try {
					iframe.contentWindow.postMessage( '{"method":"pause"}', '*' );
				} catch ( e ) {
					// Ignore errors
				}
			}
		} );
	}

	/**
	 * Handle window resize
	 */
	handleResize() {
		// Adjust video dimensions for responsive layout
		const videoWrappers = document.querySelectorAll( '.dokan-video-player-wrapper' );

		videoWrappers.forEach( ( wrapper ) => {
			const width = wrapper.offsetWidth;
			const height = width * 0.5625; // 16:9 aspect ratio

			wrapper.style.height = `${ height }px`;
		} );
	}
}

// Initialize when DOM is ready
document.addEventListener( 'DOMContentLoaded', () => {
	window.dokanProductVideoPlayer = new DokanProductVideoPlayer();
} );
/**
 * Validates an image file based on size and dimension restrictions
 *
 * @param  {File}    file - The file to validate
 * @return {Promise}      Promise that resolves if valid, rejects with error message if invalid
 */
function validateImage( file ) {
	return new Promise( ( resolve, reject ) => {
		// Validate file type
		if ( ! file.type.match( 'image.*' ) ) {
			reject( window.dokanKitsImageRestrictions.messages.invalidFile );
			return;
		}

		// Check maximum upload quantity if restrictions are enabled
		if ( window.dokanKitsImageRestrictions.hasMaximumUploadRestrictions ) {
			// Use the product_images_container selector which matches the HTML structure
			const currentImageCount = $( '#product_images_container .product_images li.image' ).length;
			if ( currentImageCount >= window.dokanKitsImageRestrictions.maxUploadCount ) {
				reject( window.dokanKitsImageRestrictions.messages.uploadLimit );
				return;
			}
		}

		// Check file size if size restrictions are enabled
		if ( window.dokanKitsImageRestrictions.hasSizeRestrictions &&
			file.size > window.dokanKitsImageRestrictions.maxSize ) {
			reject( window.dokanKitsImageRestrictions.messages.sizeError );
			return;
		}

		// Check dimensions if dimension restrictions are enabled
		if ( window.dokanKitsImageRestrictions.hasDimensionRestrictions ) {
			const img = new window.Image();
			const objectUrl = window.URL.createObjectURL( file );
			img.src = objectUrl;

			// Set a timeout to handle cases where the image might not load properly
			const timeoutId = setTimeout( () => {
				window.URL.revokeObjectURL( objectUrl );
				reject( window.dokanKitsImageRestrictions.messages.invalidFile );
			}, 10000 ); // 10 second timeout

			img.onload = function() {
				window.URL.revokeObjectURL( objectUrl );
				clearTimeout( timeoutId );

				// Get required dimensions from the settings
				const requiredWidth = parseInt( window.dokanKitsImageRestrictions.requiredWidth, 10 );
				const requiredHeight = parseInt( window.dokanKitsImageRestrictions.requiredHeight, 10 );

				// Validate dimensions
				if ( requiredWidth > 0 && requiredHeight > 0 ) {
					if ( this.width !== requiredWidth || this.height !== requiredHeight ) {
						const errorMessage = window.dokanKitsImageRestrictions.messages.dimensionError
							.replace( '{width}', this.width )
							.replace( '{height}', this.height );
						reject( errorMessage );
						return;
					}
				}

				resolve( true );
			};

			img.onerror = function() {
				window.URL.revokeObjectURL( objectUrl );
				clearTimeout( timeoutId );
				reject( window.dokanKitsImageRestrictions.messages.invalidFile );
			};
		} else {
			resolve( true );
		}
	} );
}

$( document ).ready( function() {
	/**
	 * Check image count and update Add Image button visibility
	 */
	function checkAndUpdateAddImageVisibility() {
		if ( window.dokanKitsImageRestrictions.hasMaximumUploadRestrictions ) {
			// Count existing images in the product gallery
			const imageCount = $( '#product_images_container .product_images li.image' ).length;
			const maxUploadCount = window.dokanKitsImageRestrictions.maxUploadCount;

			// Hide 'add new image' option if limit reached
			if ( imageCount >= maxUploadCount ) {
				$( '#product_images_container .product_images li.add-image' ).hide();
			} else {
				$( '#product_images_container .product_images li.add-image' ).show();
			}
		}
	}

	// Initial check on page load
	checkAndUpdateAddImageVisibility();

	// Handle click on Add Image button to prevent opening media uploader if limit reached
	$( '#product_images_container' ).on( 'click', '.add-product-images', function( e ) {
		if ( window.dokanKitsImageRestrictions.hasMaximumUploadRestrictions ) {
			const imageCount = $( '#product_images_container .product_images li.image' ).length;
			const maxUploadCount = window.dokanKitsImageRestrictions.maxUploadCount;

			if ( imageCount >= maxUploadCount ) {
				e.preventDefault();
				e.stopPropagation();
				window.alert( window.dokanKitsImageRestrictions.messages.uploadLimit );
				return false;
			}
		}
	} );

	// Set up MutationObserver to monitor gallery changes (when images are added via media uploader)
	if ( window.dokanKitsImageRestrictions.hasMaximumUploadRestrictions ) {
		const galleryContainer = document.querySelector( '#product_images_container .product_images' );
		if ( galleryContainer ) {
			const observer = new MutationObserver( function( mutations ) {
				for ( const mutation of mutations ) {
					if ( mutation.type === 'childList' ) {
						checkAndUpdateAddImageVisibility();
						break;
					}
				}
			} );

			observer.observe( galleryContainer, { childList: true } );
		}
	}

	// Handle file input change for product gallery
	$( '#product_images_container' ).on( 'change', 'input[type="file"]', function( _e ) {
		const files = this.files;
		if ( ! files || ! files.length ) {
			return;
		}

		// For each file in the selection
		for ( let i = 0; i < files.length; i++ ) {
			const file = files[ i ];

			validateImage( file ).catch( ( error ) => {
				// Using window.alert is necessary for user notification
				// eslint-disable-next-line no-alert
				window.alert( error );
				this.value = ''; // Clear the file input
				return false;
			} );
		}

		// After files are processed, update visibility of add button
		setTimeout( checkAndUpdateAddImageVisibility, 500 );
	} );

	// Handle file input change for featured image
	$( '#_product_image' ).on( 'change', function( _e ) {
		const file = this.files[ 0 ];
		if ( ! file ) {
			return;
		}

		validateImage( file )
			.then( () => {
				// Successfully validated
				// console.log( 'Featured image validated successfully' );
			} )
			.catch( ( error ) => {
				// Using window.alert is necessary for user notification
				// eslint-disable-next-line no-alert
				window.alert( error );
				this.value = '';
			} );
	} );

	// Update visibility of add image option when an image is deleted
	$( '#product_images_container' ).on( 'click', '.action-delete', function( e ) {
		e.preventDefault();

		// Wait for DOM to update after deletion
		setTimeout( function() {
			checkAndUpdateAddImageVisibility();
		}, 100 );
	} );
} );
